import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Typography, Checkbox, message } from 'antd';
import { UserOutlined, LockOutlined, QrcodeOutlined, DesktopOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginRequest } from '../services/auth';
import useDocumentTitle from '../hooks/useDocumentTitle';
import WeChatLogin from '../components/WeChatLogin';

const { Title, Text } = Typography;

const Login: React.FC = () => {
  useDocumentTitle('登录');
  const [form] = Form.useForm();
  const { login, setAuthData, state } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeLoginTab, setActiveLoginTab] = useState<'account' | 'wechat'>('account');
  const from = (location.state as any)?.from?.pathname || '/dashboard';

  useEffect(() => {
    if (state.authenticated) {
      navigate(from, { replace: true });
    }
  }, [state.authenticated, navigate, from]);

  const onFinish = async (values: LoginRequest) => {
    const success = await login(values);
    if (success) {
      navigate(from, { replace: true });
    }
  };

  const handleWeChatLoginSuccess = (userInfo: any, token: string) => {
    if (setAuthData) {
      setAuthData({ user: userInfo, token, authenticated: true });
      localStorage.setItem('auth_token', token);
      localStorage.setItem('user_info', JSON.stringify(userInfo));
      message.success('微信登录成功！');
      navigate(from, { replace: true });
    }
  };

  const handleForgotClick = () => {
    navigate('/forgot-password');
  };


  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#eef2f6',
        padding: '24px'
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '1200px',
          background: '#fff',
          borderRadius: '16px',
          overflow: 'hidden',
          display: 'flex',
          boxShadow: '0 15px 45px rgba(27, 74, 163, 0.2)'
        }}
      >
        <div
          style={{
            flex: 1,
            background: 'linear-gradient(135deg, #0d6dfb 0%, #0b5fd3 100%)',
            color: '#fff',
            padding: '48px',
            minHeight: '650px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '32px' }}>
              <img
                src="/logo-white.webp"
                alt="平克曼知产 Logo"
                style={{ height: '48px', objectFit: 'contain' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <Title level={2} style={{ color: '#fff', margin: 0, fontSize: '32px', lineHeight: 1.3 }}>
                选择平克曼，知产没困难
              </Title>
            </div>
            <Text style={{ color: '#eaf1ff', fontSize: '16px' }}>专业 · 可靠 · 安全</Text>

            <div style={{ display: 'flex', gap: '12px', marginTop: '28px', flexWrap: 'wrap' }}>
              {['知产管理', '智能监控', '数据同步'].map((tag) => (
                <span
                  key={tag}
                  style={{
                    padding: '8px 14px',
                    background: 'rgba(255,255,255,0.15)',
                    borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.25)',
                    fontWeight: 600
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <div style={{ textAlign: 'right', opacity: 0.9, position: 'relative' }}>
            <div style={{fontSize: '48px', lineHeight: '1', position: 'absolute', right: '53px', bottom: '42px'}}>
              <svg className="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="16917" width="48"
                   height="48">
                <path
                  d="M224 272c-27.2 0-48-20.8-48-48s20.8-48 48-48 48 20.8 48 48-20.8 48-48 48zM800 272c-27.2 0-48-20.8-48-48s20.8-48 48-48 48 20.8 48 48-20.8 48-48 48zM512 864c-180.8 0-344-108.8-412.8-275.2-6.4-16 1.6-35.2 17.6-41.6 16-6.4 35.2 1.6 41.6 17.6C217.6 707.2 356.8 800 512 800c155.2 0 294.4-92.8 355.2-236.8 6.4-16 25.6-24 41.6-17.6 16 6.4 24 25.6 17.6 41.6C856 755.2 692.8 864 512 864z"
                  fill="#333333" p-id="16918"></path>
              </svg>
            </div>
            <div style={{position: 'absolute', right: '12px', bottom: '34px'}}>
              <svg className="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="26216" width="48"
                   height="48">
                <path
                  d="M617.173333 609.664l226.901334 231.253333c12.373333 12.586667 12.373333 33.024 0 45.653334a31.274667 31.274667 0 0 1-44.8 0l-228.437334-232.832a270.464 270.464 0 0 1-163.84 55.296C252.970667 709.034667 128 578.986667 128 418.56 128 258.133333 252.928 128 407.04 128s279.04 130.090667 279.04 290.56c0 73.130667-26.026667 140.032-68.906667 191.104z m-210.133333 34.816c119.850667 0 217.002667-101.162667 217.002667-225.962667 0-124.8-97.152-225.962667-217.002667-225.962666-119.893333 0-217.045333 101.162667-217.045333 225.962666 0 124.8 97.194667 225.962667 217.045333 225.962667z"
                  fill="#e6e6e6" p-id="26217"></path>
                <path
                  d="M398.208 256a28.458667 28.458667 0 1 1 0 56.874667 85.333333 85.333333 0 0 0-85.333333 85.333333 28.458667 28.458667 0 1 1-56.874667 0A142.208 142.208 0 0 1 398.208 256z"
                  fill="#e6e6e6" p-id="26218"></path>
              </svg>
            </div>
          </div>
        </div>

        <div
          style={{
            flexBasis: '420px',
            padding: '48px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px'}}>
            <div>
              <Title level={3} style={{marginBottom: '8px'}}>登录管理后台</Title>
              <Text type="secondary">欢迎回来，请登录继续</Text>
            </div>
          </div>

          <div
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '86px',
              height: '86px',
              overflow: 'hidden'
            }}
          >
            <button
              aria-label={activeLoginTab === 'account' ? '切换到微信扫码登录' : '切换到账号密码登录'}
              onClick={() => setActiveLoginTab(activeLoginTab === 'account' ? 'wechat' : 'account')}
              style={{
                width: '64px',
                height: '64px',
                background: 'linear-gradient(135deg, #f7f9fc 0%, #eef2f7 100%)',
                border: 'none',
                clipPath: 'polygon(100% 0, 0 0, 100% 100%)',
                position: 'absolute',
                top: '0',
                right: '0',
                padding: 0,
                cursor: 'pointer',
                boxShadow: '0 4px 18px rgba(0,0,0,0.08)'
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '18px',
                  right: '22px',
                  color: '#0e6efb',
                  fontSize: '28px',
                  transform: 'translate(10px, -6px)'
                }}
              >
                {activeLoginTab === 'account' ? <QrcodeOutlined /> : <DesktopOutlined />}
              </div>
            </button>
          </div>

          <div
            style={{
              position: 'absolute',
              top: '10px',
              right: '90px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <div
              style={{
                position: 'relative',
                background: '#fff',
                border: '1px solid #0e6efb',
                color: '#0e6efb',
                padding: '6px 10px',
                borderRadius: '6px',
                fontSize: '13px',
                boxShadow: '0 6px 18px rgba(14,110,251,0.12)'
              }}
            >
              {activeLoginTab === 'account' ? '微信登录在这里' : '密码登录在这里'}
              <span
                style={{
                  position: 'absolute',
                  right: '-6px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 0,
                  height: 0,
                  borderTop: '6px solid transparent',
                  borderBottom: '6px solid transparent',
                  borderLeft: '6px solid #0e6efb'
                }}
              />
            </div>
          </div>

          {activeLoginTab === 'account' ? (
            <Form
              form={form}
              name="login"
              initialValues={{ remember: true }}
              onFinish={onFinish}
              autoComplete="off"
              size="large"
              layout="vertical"
            >
              <Form.Item
                label={<span style={{ fontWeight: 600 }}>用户名/邮箱</span>}
                name="login"
                rules={[{ required: true, message: '请输入用户名或邮箱!' }]}
              >
                <Input
                  prefix={<UserOutlined style={{ color: '#8c8c8c' }} />}
                  placeholder="用户名/邮箱"
                  style={{ height: '48px', borderRadius: '10px' }}
                />
              </Form.Item>

              <Form.Item
                label={<span style={{ fontWeight: 600 }}>密码</span>}
                name="password"
                rules={[{ required: true, message: '请输入密码!' }]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: '#8c8c8c' }} />}
                  placeholder="请输入登录密码"
                  style={{ height: '48px', borderRadius: '10px' }}
                />
              </Form.Item>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <Form.Item name="remember" valuePropName="checked" noStyle>
                <Checkbox>记住我</Checkbox>
              </Form.Item>
              <a style={{ color: '#1890ff' }} onClick={handleForgotClick}>忘记密码？</a>
            </div>

              <Button
                type="primary"
                htmlType="submit"
                loading={state.loading}
                style={{
                  width: '100%',
                  height: '48px',
                  borderRadius: '10px',
                  background: 'linear-gradient(90deg, #0e6efb 0%, #075edb 100%)',
                  fontWeight: 700,
                  letterSpacing: '2px'
                }}
              >
                登录
              </Button>
            </Form>
          ) : (
            <div style={{ marginTop: '8px' }}>
              <WeChatLogin
                visible={activeLoginTab === 'wechat'}
                onSuccess={handleWeChatLoginSuccess}
                mode="login"
                embed
              />
            </div>
          )}

          <div style={{ marginTop: '32px', textAlign: 'center', color: '#999' }}>
            © 2025 平克曼知产
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
